import React from 'react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { cx, focusRing } from './cx';

export interface ChipProps {
  children: React.ReactNode;
  /** Makes the chip a toggle button; `selected` is its state. */
  onClick?: () => void;
  selected?: boolean;
  /** Adds a ✕ that removes the chip; `removeLabel` names it for screen readers. */
  onRemove?: () => void;
  removeLabel?: string;
  /** 'sm' for a chip inside a line of text, such as a chore's labels. */
  size?: 'sm' | 'md';
  className?: string;
}

/** A rounded pill: a label on a chore, a filter that can be toggled, or an active filter to clear. */
export function Chip({ children, onClick, selected = false, onRemove, removeLabel, size = 'md', className }: ChipProps) {
  const look = cx(
    'inline-flex items-center gap-1 rounded-full border text-xs font-medium transition-colors',
    size === 'sm' ? 'h-5 px-2' : 'h-6 px-2.5',
    selected
      ? 'border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-400/30 dark:bg-indigo-500/15 dark:text-indigo-300'
      : 'border-transparent bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
    onClick && !selected && 'hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-700 dark:hover:text-gray-100',
    onRemove && 'pr-1',
    className,
  );

  const remove = onRemove && (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onRemove(); }}
      aria-label={removeLabel ?? 'Remove'}
      className={cx('rounded-full p-0.5 opacity-70 hover:bg-black/5 dark:hover:bg-white/10 hover:opacity-100', focusRing)}
    >
      <XMarkIcon className="h-3 w-3" aria-hidden="true" />
    </button>
  );

  if (onClick) {
    return (
      <button type="button" aria-pressed={selected} onClick={onClick} className={cx(look, focusRing)}>
        {children}
        {remove}
      </button>
    );
  }
  return <span className={look}>{children}{remove}</span>;
}
